import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { BUDGET_STAGES, GlobalValuesService, getSafeImageUrl } from '@app/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  CdkDragDrop,
  CdkDragMove,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';

type KanbanCard = {
  id: number | string;
  sourceItem: any;
  title: string;
  code: string;
  subtitle: string;
  amount: number | null;
  tags: string[];
  avatarText: string;
  avatarUrl: string | undefined;
  avatarTooltip: string;
  rating: boolean[];
};

type KanbanCardGroup = {
  key: string;
  title: string;
  cards: KanbanCard[];
};

type KanbanColumn = {
  key: string;
  title: string;
  order: number;
  isGlobal?: boolean;
  teamIds?: Array<number | string> | null;
  cards: KanbanCard[];
  groups: KanbanCardGroup[];
};

type FlattenedKanbanItem = {
  item: any;
  groupTitle: string;
};

export type KanbanGroupingCriterion = {
  field: string;
  label: string;
  order: number;
};

export type GeneralStage = {
  id?: number | string;
  key: string;
  title: string;
  order: number;
  isGlobal?: boolean;
  teamIds?: Array<number | string> | null;
};

export type KanbanStageDrop = {
  item: any;
  sourceStage: GeneralStage;
  targetStage: GeneralStage;
};

export type KanbanStageAdd = {
  targetStage: GeneralStage;
};

@Component({
  selector: 'app-layout-kanban',
  standalone: false,
  templateUrl: './layout-kanban.component.html',
  styleUrl: './layout-kanban.component.less',
})
export class LayoutKanbanComponent {
  private destroyRef = inject(DestroyRef);
  @ViewChild('kanbanBoard') kanbanBoard?: ElementRef<HTMLElement>;

  @Input() set maestroName(value: string) {
    this.currentMaestroName = value || '';
    this.columns = this.buildColumns(this.currentData);
  }
  @Input() set data(value: any[]) {
    this.currentData = value || [];
    this.columns = this.buildColumns(this.currentData);
  }
  @Input() showAddCard = true;
  @Input() set groupingActive(value: boolean) {
    if (this.currentGroupingActive === value) {
      return;
    }

    this.currentGroupingActive = value;
    this.columns = this.buildColumns(this.currentData);
  }
  get groupingActive(): boolean {
    return this.currentGroupingActive;
  }
  @Input() set groupingCriteria(value: KanbanGroupingCriterion[]) {
    const nextCriteria = value || [];
    if (this.areGroupingCriteriaEqual(this.currentGroupingCriteria, nextCriteria)) {
      return;
    }

    this.currentGroupingCriteria = nextCriteria;
    this.columns = this.buildColumns(this.currentData);
  }
  @Output() stageDrop = new EventEmitter<KanbanStageDrop>();
  @Output() stageAdd = new EventEmitter<KanbanStageAdd>();
  @Output() cardClick = new EventEmitter<any>();

  columns: KanbanColumn[] = [];
  private currentData: any[] = [];
  private currentMaestroName: string = '';
  private currentGroupingActive = false;
  private currentGroupingCriteria: KanbanGroupingCriterion[] = [];
  private readonly horizontalScrollEdge = 96;
  private readonly horizontalScrollMaxStep = 28;
  private readonly cardClickMovementThreshold = 6;
  private pointerStartPosition: { x: number; y: number } | null = null;

  private readonly opportunityStages: GeneralStage[] = [
    { key: 'Nuevo', title: 'Nuevo', order: 1 },
    { key: 'Evaluación', title: 'Evaluación', order: 2 },
    { key: 'Presupuesto', title: 'Presupuesto', order: 3 },
    { key: 'Negociación de cierre', title: 'Negociación de cierre', order: 4 },
    { key: 'Pausada', title: 'Pausada', order: 5 },
    { key: 'Adjudicada', title: 'Adjudicada', order: 6 },
    { key: 'Ejecutada', title: 'Ejecutada', order: 7 },
    { key: 'Cancelada', title: 'Cancelada', order: 8 },
  ];

  constructor(private globalValuesService: GlobalValuesService) {
    this.globalValuesService.stages$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.isBudgetView()) {
          this.columns = this.buildColumns(this.currentData);
        }
      });
  }

  trackColumn(_: number, column: KanbanColumn): string {
    return column.key;
  }

  trackCard(_: number, card: KanbanCard): number | string {
    return card.id;
  }

  trackGroup(_: number, group: KanbanCardGroup): string {
    return group.key;
  }

  private areGroupingCriteriaEqual(
    current: KanbanGroupingCriterion[],
    next: KanbanGroupingCriterion[],
  ): boolean {
    if (current.length !== next.length) {
      return false;
    }

    return current.every((criteria, index) => {
      const nextCriteria = next[index];
      return criteria.field === nextCriteria.field &&
        criteria.label === nextCriteria.label &&
        criteria.order === nextCriteria.order;
    });
  }

  onCardPointerDown(event: PointerEvent): void {
    this.pointerStartPosition = {
      x: event.clientX,
      y: event.clientY,
    };
  }

  onCardPointerUp(event: PointerEvent, card: KanbanCard): void {
    if (!this.pointerStartPosition) {
      return;
    }

    const deltaX = event.clientX - this.pointerStartPosition.x;
    const deltaY = event.clientY - this.pointerStartPosition.y;
    const distance = Math.hypot(deltaX, deltaY);
    this.pointerStartPosition = null;

    if (distance > this.cardClickMovementThreshold) {
      return;
    }

    this.cardClick.emit(card.sourceItem);
  }

  onCardDragMoved(event: CdkDragMove<KanbanCard>): void {
    const board = this.kanbanBoard?.nativeElement;
    if (!board) {
      return;
    }

    const rect = board.getBoundingClientRect();
    const pointerX = event.pointerPosition.x;
    const leftDistance = pointerX - rect.left;
    const rightDistance = rect.right - pointerX;

    if (leftDistance >= 0 && leftDistance < this.horizontalScrollEdge) {
      const intensity = (this.horizontalScrollEdge - leftDistance) / this.horizontalScrollEdge;
      board.scrollLeft -= Math.ceil(intensity * this.horizontalScrollMaxStep);
      return;
    }

    if (rightDistance >= 0 && rightDistance < this.horizontalScrollEdge) {
      const intensity = (this.horizontalScrollEdge - rightDistance) / this.horizontalScrollEdge;
      board.scrollLeft += Math.ceil(intensity * this.horizontalScrollMaxStep);
    }
  }

  onAddCard(column: KanbanColumn): void {
    this.stageAdd.emit({
      targetStage: {
        id: column.key,
        key: column.key,
        title: column.title,
        order: column.order,
        isGlobal: column.isGlobal,
        teamIds: column.teamIds,
      },
    });
  }

  refreshFromData(): void {
    this.columns = this.buildColumns(this.currentData);
  }

  dropCard(event: CdkDragDrop<KanbanColumn>): void {
    const card = event.item.data as KanbanCard;
    const sourceColumn = event.previousContainer.data;
    const targetColumn = event.container.data;

    if (!card?.sourceItem || !sourceColumn || !targetColumn) {
      return;
    }

    if (sourceColumn.key === targetColumn.key) {
      moveItemInArray(
        targetColumn.cards,
        event.previousIndex,
        event.currentIndex
      );
      this.syncUngroupedColumn(targetColumn);
      return;
    }

    transferArrayItem(
      sourceColumn.cards,
      targetColumn.cards,
      event.previousIndex,
      event.currentIndex
    );
    this.syncUngroupedColumn(sourceColumn);
    this.syncUngroupedColumn(targetColumn);

    this.stageDrop.emit({
      item: card.sourceItem,
      sourceStage: {
        id: sourceColumn.key,
        key: sourceColumn.key,
        title: sourceColumn.title,
        order: sourceColumn.order,
        isGlobal: sourceColumn.isGlobal,
        teamIds: sourceColumn.teamIds,
      },
      targetStage: {
        id: targetColumn.key,
        key: targetColumn.key,
        title: targetColumn.title,
        order: targetColumn.order,
        isGlobal: targetColumn.isGlobal,
        teamIds: targetColumn.teamIds,
      },
    });
  }

  private buildColumns(data: any[]): KanbanColumn[] {
    const cards = this.flattenRows(data);
    const columnsMap = this.getBaseColumns();

    cards.forEach(({ item, groupTitle }) => {
      const stage = this.resolveStage(item);
      if (!stage) {
        return;
      }

      const column = columnsMap.get(stage.key) || {
        key: stage.key,
        title: stage.title,
        order: stage.order,
        isGlobal: stage.isGlobal,
        teamIds: stage.teamIds,
        cards: [],
        groups: [],
      };

      const card = this.mapCard(item);
      column.cards.push(card);
      this.addCardToGroup(column, card, groupTitle);
      column.order = Math.min(column.order, stage.order);
      columnsMap.set(stage.key, column);
    });

    return Array.from(columnsMap.values()).sort((a, b) => {
      if (a.order !== b.order) {
        return a.order - b.order;
      }
      return a.title.localeCompare(b.title);
    });
  }

  private getBaseColumns(): Map<string, KanbanColumn> {
    const columnsMap = new Map<string, KanbanColumn>();
    const stages = this.isBudgetView()
      ? BUDGET_STAGES.map((stage) => this.toGeneralStage(stage))
      : this.getSystemOpportunityStages();

    stages.forEach((stage) => {
      columnsMap.set(stage.key, {
        key: stage.key,
        title: stage.title,
        order: stage.order,
        isGlobal: stage.isGlobal,
        teamIds: stage.teamIds,
        cards: [],
        groups: [],
      });
    });

    return columnsMap;
  }

  private flattenRows(data: any[]): FlattenedKanbanItem[] {
    const rows: FlattenedKanbanItem[] = [];

    data.forEach((row) => {
      this.collectRows(row, rows, []);
    });

    return rows;
  }

  private collectRows(row: any, rows: FlattenedKanbanItem[], groupPath: string[]): void {
    if (!row) {
      return;
    }

    if (this.isGroupedRow(row)) {
      const nextGroupPath = this.groupingActive
        ? [...groupPath, this.getGroupTitle(row)].filter((label) => !!label)
        : groupPath;

      row.items.forEach((item: any) => {
        if (this.isGroupedRow(item)) {
          this.collectRows(item, rows, nextGroupPath);
          return;
        }

        rows.push({
          item,
          groupTitle: this.resolveCardGroupTitle(item, nextGroupPath),
        });
      });
      return;
    }

    rows.push({
      item: row,
      groupTitle: this.resolveCardGroupTitle(row, groupPath),
    });
  }

  private resolveCardGroupTitle(item: any, groupPath: string[]): string {
    if (!this.groupingActive) {
      return '';
    }

    if (this.currentGroupingCriteria.length > 0) {
      return this.currentGroupingCriteria
        .map((criteria) => this.resolveGroupValueLabel(item, criteria))
        .filter((label) => !!label)
        .join(' / ');
    }

    return groupPath.join(' / ');
  }

  private resolveGroupValueLabel(item: any, criteria: KanbanGroupingCriterion): string {
    const rawValue = this.getNestedValue(item, criteria.field);
    const label = this.cleanText(this.getKnownGroupLabel(item, criteria.field, rawValue));

    return label || 'Sin asignar';
  }

  private getKnownGroupLabel(item: any, field: string, value: any): string {
    if (field === 'userId') {
      const commercial = this.resolveCommercial(item);
      return this.resolveCommercialName(commercial, item) || this.findUserName(value);
    }

    if (field === 'companyId') {
      return this.cleanText(item?.company?.name || item?.opportunity?.company?.name || item?.originalOpportunity?.company?.name) ||
        this.findNameById(this.globalValuesService.companies$.value, value);
    }

    if (field === 'partnerId') {
      return this.getPartnerName(item) || this.findNameById(this.globalValuesService.partnerlist, value);
    }

    if (field === 'teamId') {
      return this.cleanText(item?.salesTeam || item?.team?.name || item?.opportunity?.team?.name) ||
        this.findNameById(this.globalValuesService.salesTeams$.value, value);
    }

    if (field === 'countryId') {
      return this.cleanText(item?.country?.name || item?.country) ||
        this.findNameById(this.globalValuesService.countries$.value, value);
    }

    if (field === 'lostReasonId') {
      return this.cleanText(item?.lostReason?.name || item?.lostReason) ||
        this.findNameById(this.globalValuesService.lossReasons$.value, value);
    }

    if (field === 'city') {
      return this.cleanText(item?.city || value);
    }

    if (this.isDateGroupingField(field)) {
      return this.formatGroupDate(value);
    }

    return this.cleanText(value);
  }

  private addCardToGroup(column: KanbanColumn, card: KanbanCard, groupTitle: string): void {
    const title = this.groupingActive && groupTitle ? groupTitle : '';
    const key = title || '__ungrouped__';
    let group = column.groups.find((item) => item.key === key);

    if (!group) {
      group = {
        key,
        title,
        cards: [],
      };
      column.groups.push(group);
    }

    group.cards.push(card);
  }

  private syncUngroupedColumn(column: KanbanColumn): void {
    if (this.groupingActive) {
      return;
    }

    column.groups = [{
      key: '__ungrouped__',
      title: '',
      cards: column.cards,
    }];
  }

  private resolveStage(item: any): GeneralStage | null {
    if (this.isBudgetView()) {
      return this.resolveBudgetStage(item);
    }

    const stageId = item?.stageId ?? item?.stage_id;
    const stage = this.findStage(stageId);
    if (!stage) {
      return null;
    }

    if (this.isLeadsView() && !this.isStageAvailableForLeads(stage)) {
      return null;
    }

    return this.mapSystemStage(stage);
  }

  formatMoney(value: number | null): string {
    if (value === null) {
      return '';
    }

    return `$${Math.round(value).toLocaleString('es-CL')}`;
  }

  private resolveBudgetStage(item: any): GeneralStage | null {
    const stageValue = item?.stage || item?.originalBudget?.stage || item?.state;
    const budgetStage = BUDGET_STAGES.find((stage) => stage.value === stageValue);

    if (budgetStage) {
      return this.toGeneralStage(budgetStage);
    }

    if (item?.dateCancelled || item?.state === 'Cancelled') {
      return this.toGeneralStage(BUDGET_STAGES.find((stage) => stage.value === 'Cancelled')!);
    }

    if (item?.dateConfirm) {
      return this.toGeneralStage(BUDGET_STAGES.find((stage) => stage.value === 'SaleOrder')!);
    }

    return null;
  }

  /**
   * `key = value` — resolution keys on the backend enum value, never on column
   * order/position. `order` is the enum's own numeric identity (`enumValue`), so no
   * separate ordering table can drift out of sync with it.
   */
  private toGeneralStage(stage: (typeof BUDGET_STAGES)[number]): GeneralStage {
    return {
      key: stage.value,
      title: stage.name,
      order: stage.enumValue,
    };
  }

  private getSystemOpportunityStages(): GeneralStage[] {
    const stages = ((this.globalValuesService.stages$.value || []) as any[])
      .filter((stage: any) => !this.isLeadsView() || this.isStageAvailableForLeads(stage))
      .map((stage: any) => this.mapSystemStage(stage))
      .sort((a: GeneralStage, b: GeneralStage) => {
        if (a.order !== b.order) {
          return a.order - b.order;
        }
        return a.title.localeCompare(b.title);
      });

    return stages.length > 0 ? stages : (this.isLeadsView() ? [] : this.opportunityStages);
  }

  private mapSystemStage(stage: any): GeneralStage {
    const id = stage?.id;
    const order = this.getStageOrder(stage) || this.toOrder(stage?.sequence);
    const title = this.cleanText(stage?.name || stage?.label || stage?.title || 'Sin nombre');

    return {
      id,
      key: id !== null && id !== undefined ? String(id) : `${title}-${order}`,
      title,
      order,
      isGlobal: stage?.isGlobal === true,
      teamIds: this.getStageTeamIds(stage),
    };
  }

  private findStage(stageId: number | string | null | undefined): any {
    if (stageId === null || stageId === undefined) {
      return null;
    }

    const numericStageId = Number(stageId);
    return (this.globalValuesService.stages$.value || []).find(
      (stage: any) => Number(stage.id) === numericStageId,
    );
  }

  private getStageOrder(stage: any): number | null {
    if (!stage) {
      return null;
    }

    const order = stage.order ?? stage.orderIndex ?? stage.sequence ?? stage.displayOrder;
    const numericOrder = Number(order);
    return Number.isFinite(numericOrder) && numericOrder > 0 ? numericOrder : null;
  }

  private getStageTeamIds(stage: any): Array<number | string> | null {
    if (!stage) {
      return null;
    }

    if (Array.isArray(stage.teamIds)) {
      return stage.teamIds
        .map((team: any) => team?.id ?? team)
        .filter((teamId: any) => teamId !== null && teamId !== undefined);
    }

    if (Array.isArray(stage.teams)) {
      return stage.teams
        .map((team: any) => team?.id ?? team)
        .filter((teamId: any) => teamId !== null && teamId !== undefined);
    }

    return null;
  }

  private mapCard(item: any): KanbanCard {
    const title = this.resolveTitle(item);
    const subtitle = this.resolveSubtitle(item);
    const amount = this.resolveAmount(item);
    const tags = this.resolveTags(item);
    const commercial = this.resolveCommercial(item);
    const commercialName = this.resolveCommercialName(commercial, item);

    return {
      id: item?.id || item?.originalId || `${title}-${subtitle}`,
      sourceItem: item,
      title,
      code: this.resolveCode(item),
      subtitle,
      amount,
      tags,
      avatarText: this.getAvatarText(commercialName || title),
      avatarUrl: this.resolveCommercialAvatar(commercial),
      avatarTooltip: commercialName,
      rating: this.resolveRating(item),
    };
  }

  private resolveTitle(item: any): string {
    if (this.isBudgetView()) {
      return this.cleanText(item?.nombre || item?.name || 'Presupuesto');
    }

    return this.cleanText(
      item?.oportunidad ||
        item?.originalOpportunityName ||
        item?.name ||
        item?.contactPartner?.name ||
        'Sin nombre',
    );
  }

  private resolveCode(item: any): string {
    if (!this.isBudgetView()) {
      return '';
    }

    return '';
  }

  private resolveSubtitle(item: any): string {
    if (this.isBudgetView()) {
      return this.resolveClientCompany(item);
    }

    return this.resolveClientCompany(item);
  }

  private resolveClientCompany(item: any): string {
    const customerCompanyName =
      this.getEntityDisplayName(item?.customerCompany) ||
      this.getEntityDisplayName(item?.lead?.customerCompany) ||
      this.getEntityDisplayName(item?.originalBudget?.customerCompany) ||
      this.getEntityDisplayName(item?.originalBudget?.lead?.customerCompany);
    const displayName = this.cleanText(
      customerCompanyName ||
        item?.clientCompanyDisplay ||
        item?.clientCompany ||
        '',
    );
    if (displayName && displayName !== '-') {
      return displayName;
    }

    const companyId =
      item?.clientCompanyId ??
      item?.opportunity?.clientCompanyId ??
      item?.originalOpportunity?.clientCompanyId ??
      item?.lead?.clientCompanyId ??
      item?.originalBudget?.lead?.clientCompanyId ??
      null;
    const companyName = this.findNameById(this.globalValuesService.partnerlist, companyId);
    if (companyName) {
      return companyName;
    }

    const directName = this.cleanText(
      item?.opportunity?.clientCompany ||
        item?.originalOpportunity?.clientCompany ||
        item?.lead?.clientCompany ||
        item?.originalBudget?.lead?.clientCompany ||
        '',
    );
    if (directName && directName !== '-') {
      return directName;
    }

    const contactParentId =
      item?.contactPartner?.parentId ??
      item?.partner?.parentId ??
      item?.opportunity?.contactPartner?.parentId ??
      item?.opportunity?.partner?.parentId ??
      item?.originalOpportunity?.contactPartner?.parentId ??
      item?.originalOpportunity?.partner?.parentId ??
      item?.lead?.contactPartner?.parentId ??
      item?.lead?.partner?.parentId ??
      item?.originalBudget?.lead?.contactPartner?.parentId ??
      item?.originalBudget?.lead?.partner?.parentId ??
      null;

    return this.findNameById(this.globalValuesService.partnerlist, contactParentId);
  }

  private resolveAmount(item: any): number | null {
    const value = this.isBudgetView()
      ? item?.total
      : item?.ingreso ?? item?.expectedRevenue?.parsedValue ?? item?.expectedRevenue;

    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  private resolveTags(item: any): string[] {
    const tags = [
      item?.stageData?.label,
      this.isBudgetView() ? item?.aprobada : item?.salesTeam,
      item?.clientCompany,
      item?.company?.name,
      item?.contacto,
    ]
      .map((tag) => this.cleanText(tag || ''))
      .filter((tag) => !!tag && tag !== '-');

    return Array.from(new Set(tags)).slice(0, 3);
  }

  private resolveCommercial(item: any): any {
    return (
      item?.comercialUser ||
      item?.commercialUser ||
      item?.user?.partner ||
      item?.opportunity?.comercialUser ||
      item?.opportunity?.user?.partner ||
      item?.originalOpportunity?.comercialUser ||
      item?.originalOpportunity?.user?.partner ||
      item?.lead?.comercialUser ||
      item?.lead?.user?.partner ||
      item?.originalBudget?.lead?.comercialUser ||
      item?.originalBudget?.lead?.user?.partner ||
      null
    );
  }

  private resolveCommercialName(commercial: any, item: any): string {
    const objectName = this.cleanText(
      [commercial?.name, commercial?.surName].filter((value) => !!value).join(' '),
    );

    return this.cleanText(objectName || item?.comercial || item?.commercialName || '');
  }

  private findUserName(userId: any): string {
    const user = (this.globalValuesService.users$.value || []).find(
      (item: any) => Number(item?.id) === Number(userId),
    );
    return this.cleanText(
      [user?.partner?.name, user?.partner?.surName].filter((value) => !!value).join(' '),
    );
  }

  private findNameById(list: any[] | undefined | null, id: any): string {
    if (id === null || id === undefined || id === '') {
      return '';
    }

    const match = (list || []).find((item: any) => Number(item?.id) === Number(id));
    return this.cleanText(match?.name || match?.label || match?.title || '');
  }

  private getPartnerName(item: any): string {
    const partner = item?.contactPartner || item?.partner || item?.opportunity?.partner || item?.lead?.partner;
    return this.cleanText(
      [partner?.name, partner?.surName].filter((value) => !!value).join(' ') ||
        item?.contacto ||
        item?.contactName,
    );
  }

  private getNestedValue(item: any, path: string): any {
    if (!path) {
      return null;
    }

    const directValue = item?.[path];
    if (directValue !== undefined && directValue !== null && directValue !== '') {
      return directValue;
    }

    return path.split('.').reduce((value, key) => value?.[key], item);
  }

  private isDateGroupingField(field: string): boolean {
    return ['createdOn', 'conversionDate', 'expectedCloseDate', 'dateLost'].includes(field);
  }

  private formatGroupDate(value: any): string {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return this.cleanText(value);
    }

    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  private resolveCommercialAvatar(commercial: any): string | undefined {
    return commercial?.avatar ? getSafeImageUrl(commercial.avatar) : undefined;
  }

  private resolveRating(item: any): boolean[] {
    const priority =
      item?.priority ||
      item?.opportunity?.priority ||
      item?.originalOpportunity?.priority ||
      item?.lead?.priority ||
      item?.originalBudget?.lead?.priority;
    const filledStars = this.getPriorityStarsCount(priority);

    return [0, 1, 2, 3].map((index) => index < filledStars);
  }

  private getPriorityStarsCount(priority: any): number {
    if (priority === null || priority === undefined || priority === '') {
      return 0;
    }

    if (typeof priority === 'number') {
      return Math.max(0, Math.min(4, priority));
    }

    const normalizedPriority = this.normalizeStageName(priority);
    const priorityMap: Record<string, number> = {
      low: 1,
      baja: 1,
      medium: 2,
      media: 2,
      high: 3,
      alta: 3,
      veryhigh: 4,
      very_high: 4,
      muyalta: 4,
      'muy alta': 4,
    };

    return priorityMap[normalizedPriority] || 0;
  }

  private getAvatarText(title: string): string {
    const normalized = this.cleanText(title);
    if (!normalized) {
      return '?';
    }

    const words = normalized
      .split(/\s+/)
      .filter((word) => !!word);

    if (words.length === 1) {
      return words[0].slice(0, 2).toUpperCase();
    }

    return `${words[0].charAt(0)}${words[1].charAt(0)}`.toUpperCase();
  }

  private cleanText(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    return String(value).trim();
  }

  private getEntityDisplayName(entity: any): string {
    if (entity === null || entity === undefined || entity === '') {
      return '';
    }
    if (typeof entity === 'string') {
      return entity.trim();
    }

    const name = `${entity.name || ''} ${entity.surName || ''}`.trim();
    return name || entity.businessName || entity.displayName || '';
  }

  private normalizeStageName(value: string): string {
    return this.cleanText(value)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  }

  private toOrder(value: any): number {
    const order = Number(value);
    return Number.isFinite(order) && order > 0 ? order : 999;
  }

  isBudgetView(): boolean {
    return this.currentMaestroName === 'Presupuestos' || this.currentMaestroName === 'Cotizaciones';
  }

  getAddCardLabel(): string {
    if (this.isLeadsView()) {
      return 'Añadir lead';
    }

    if (this.isBudgetView()) {
      return 'Añadir presupuesto';
    }

    return 'Añadir oportunidad';
  }

  getEmptyTitle(): string {
    if (this.isLeadsView()) {
      return 'No hay leads';
    }

    if (this.isBudgetView()) {
      return 'No hay presupuestos';
    }

    return 'No hay oportunidades';
  }

  getEmptyDescription(): string {
    if (this.isLeadsView()) {
      return 'Añade un nuevo lead para comenzar.';
    }

    if (this.isBudgetView()) {
      return 'Añade un nuevo presupuesto para comenzar.';
    }

    return 'Añade una nueva oportunidad para comenzar.';
  }

  hasGroupTitle(group: KanbanCardGroup): boolean {
    return this.groupingActive && !!group.title;
  }

  private isLeadsView(): boolean {
    return this.currentMaestroName === 'Leads';
  }

  private isGroupedRow(row: any): boolean {
    return Array.isArray(row?.items) &&
      row.items.length > 0 &&
      row.items.some((item: any) => this.looksLikeKanbanRecord(item) || this.isGroupedRow(item));
  }

  private looksLikeKanbanRecord(row: any): boolean {
    return (
      row?.stageId !== undefined ||
      row?.stage_id !== undefined ||
      row?.StageId !== undefined ||
      row?.stage !== undefined ||
      row?.type === 'Lead' ||
      row?.type === 'Opportunity' ||
      row?.expectedRevenue !== undefined ||
      row?.partnerId !== undefined ||
      row?.clientCompanyId !== undefined
    );
  }

  private isStageAvailableForLeads(stage: any): boolean {
    const value = stage?.useForLeads ?? stage?.UseForLeads ?? stage?.use_for_leads;

    return value === true || value === 'true' || value === 'True' || value === 1 || value === '1';
  }

  private getGroupTitle(row: any): string {
    const rawTitle = row?.groupName || row?.oportunidad || row?.name || '';
    return this.cleanText(rawTitle);
  }
}
